/**
 * The members of a conversation as the composer and the roster read them (conversation-members spec, owner
 * 2026-10-29): every participant is a member, its membership id is its address, and a message goes only to the members
 * it names — with none named, to the conversation's lead, and with no lead, to no one. An AI member that receives a
 * message runs a model cycle, so the composer always shows who the next message is for. Pure helpers.
 */
import {
  type ConversationMemberRef,
  isAiMemberType,
  MAX_AI_RECIPIENTS,
  memberKindLabel,
} from '@confluent/shared/lib/conversation-addressing';
import type { ConversationMember } from '../../generated/api/types.gen.js';
import type { StreamMessage } from '../../hooks/useConversationStream.js';

/** A member as the list of members returns it, by its address. */
export function toMemberRef(member: ConversationMember): ConversationMemberRef {
  return {
    id: member.id,
    memberType: member.memberType,
    memberId: member.memberId,
    name: member.displayName ?? `${memberKindLabel(member.memberType)} ${member.memberId}`,
    role: member.role,
  };
}

/** Is this member the person looking at the conversation? */
export function isMe(member: Pick<ConversationMemberRef, 'memberType' | 'memberId'>, userId: string | null): boolean {
  return member.memberType === 'user' && userId !== null && String(member.memberId) === userId;
}

/** A question a member put to me that I have not answered: my next message answers it, back to its writer. */
export interface PendingAsk {
  /** The message (by id): what a reply names in `answers`. */
  messageId: number;
  author: ConversationMemberRef;
}

/** The newest message, written by someone else, whose delivery to me is still open. */
export function pendingAskForMe(messages: readonly StreamMessage[], userId: string | null): PendingAsk | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    const author = message.author;
    if (!author || isMe(author, userId)) continue;
    const mine = (message.to ?? []).find((recipient) => isMe(recipient, userId) && recipient.status === 'pending');
    if (mine) return { messageId: Number(message.id), author };
  }
  return null;
}

/**
 * What a chip inside a message answers (owner 2026-10-31: the person pressed an option on the Desk Lead's question and
 * the reply went to the lead instead). A card's chips belong to the member who wrote the message: when that message was
 * addressed to me, the chip answers it — back to its writer — whatever the composer's pick; otherwise it is an ordinary
 * message (the lead).
 */
export function answersFor(message: StreamMessage | undefined, userId: string | null): number | undefined {
  const author = message?.author;
  if (!message || !author || isMe(author, userId)) return undefined;
  const id = Number(message.id);
  if (!Number.isSafeInteger(id) || id <= 0) return undefined;
  return (message.to ?? []).some((recipient) => isMe(recipient, userId)) ? id : undefined;
}

/** The members working on a message right now (a delivery `running`), by address. */
export function workingMemberIds(messages: readonly StreamMessage[]): Set<number> {
  const working = new Set<number>();
  for (const message of messages) {
    for (const recipient of message.to ?? []) if (recipient.status === 'running') working.add(recipient.id);
  }
  return working;
}

/** Addresses the messages name (authors and recipients) that the list of members does not hold yet. */
export function unknownMemberIds(
  messages: readonly StreamMessage[],
  members: readonly Pick<ConversationMemberRef, 'id'>[],
): number[] {
  const known = new Set(members.map((member) => member.id));
  const unknown = new Set<number>();
  for (const message of messages) {
    if (message.author && !known.has(message.author.id)) unknown.add(message.author.id);
    for (const recipient of message.to ?? []) if (!known.has(recipient.id)) unknown.add(recipient.id);
  }
  return [...unknown].sort((a, b) => a - b);
}

/** Who the next message is for when the person picked no one. */
export type DefaultRecipient =
  | { kind: 'answering'; member: ConversationMemberRef; messageId: number }
  | { kind: 'lead'; member: ConversationMemberRef }
  | { kind: 'nobody' };

export function defaultRecipient(
  pending: PendingAsk | null,
  lead: ConversationMemberRef | null,
  me: ConversationMemberRef | null,
): DefaultRecipient {
  if (pending) return { kind: 'answering', member: pending.author, messageId: pending.messageId };
  if (lead && lead.id !== me?.id) return { kind: 'lead', member: lead };
  return { kind: 'nobody' };
}

/** What a reply carries: the members picked, and the open question it answers. */
export interface ComposerAddress {
  to?: number[];
  answers?: number;
}

/**
 * `chosen` null (or empty) keeps the default: the open question is answered back to its writer, else the server sends
 * the message to the lead. A pick names its members; it still answers the open question when it names its writer.
 */
export function addressOf(chosen: readonly number[] | null, pending: PendingAsk | null): ComposerAddress {
  if (!chosen || chosen.length === 0) return pending ? { answers: pending.messageId } : {};
  return {
    to: [...chosen],
    ...(pending && chosen.includes(pending.author.id) ? { answers: pending.messageId } : {}),
  };
}

/**
 * The pick without the members who left since it was made (review W-24, 2026-11-01): a departed member is never sent
 * to, nor shown as "Member <id>". A pick left empty keeps the default, as no pick does.
 */
export function stillPicked(
  chosen: readonly number[] | null,
  members: readonly ConversationMemberRef[],
): number[] | null {
  if (!chosen) return null;
  const present = new Set(members.map((member) => member.id));
  return chosen.filter((id) => present.has(id));
}

/** Add or drop one member from the pick; an AI member past the cap is refused (each one runs a model cycle). */
export function togglePick(
  chosen: readonly number[] | null,
  member: ConversationMemberRef,
  members: readonly ConversationMemberRef[],
): number[] {
  const current = chosen ?? [];
  if (current.includes(member.id)) return current.filter((id) => id !== member.id);
  if (isAiMemberType(member.memberType) && aiPicked(current, members) >= MAX_AI_RECIPIENTS) return [...current];
  return [...current, member.id];
}

/** How many AI members the pick names. */
export function aiPicked(chosen: readonly number[], members: readonly ConversationMemberRef[]): number {
  const byId = new Map(members.map((member) => [member.id, member]));
  return chosen.filter((id) => {
    const member = byId.get(id);
    return member !== undefined && isAiMemberType(member.memberType);
  }).length;
}

/** Can one more AI member be picked? */
export function canPickAi(chosen: readonly number[] | null, members: readonly ConversationMemberRef[]): boolean {
  return aiPicked(chosen ?? [], members) < MAX_AI_RECIPIENTS;
}
